import React from 'react';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import { flexLeftColumn } from '@seedlang/style_mixins';
import { TableWrapper } from 'components/table_wrapper';
import Paginator from 'components/paginator';
import { orderBy } from 'lodash';

const PAYMENTS_PER_PAGE = 5;

const Wrapper = styled.div`
  ${flexLeftColumn()};
  align-items: stretch;
  gap: 20px;

  table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    th {
      text-align: left;
      background: ${Theme.lightGray};
      color: ${Theme.darkGray};
      font-size: 13px;
      font-weight: 800;
      padding: 10px 14px;
    }
    td {
      font-size: 16px;
      font-family: inherit;
      line-height: 20px;
      padding: 14px;
      border-bottom: 1px solid ${Theme.lightGray};
      i {
        margin-top: 4px;
      }
      &:first-of-type {
        width: 85%;
      }
    }
    th:last-of-type,
    td:last-of-type {
      text-align: right;
      white-space: nowrap;
      width: 15%;
    }
  }
`;

const Plan = styled.div`
  font-weight: bold;
`;

const PaymentRow = styled.tr`
  td {
    transition: background-color 150ms ease;
  }

  &[aria-current='true'] td {
    background: ${Theme.lightBlue};
  }

  &:hover td {
    background: #f7fafb;
  }

  &[aria-current='true']:hover td {
    background: #cff3fb;
  }
`;

const CurrentPaymentLabel = styled.span`
  color: ${Theme.blue};
  font-size: 12px;
  margin-left: 10px;
`;

const PendingPaymentLabel = styled(CurrentPaymentLabel)`
  color: ${Theme.darkGray};
`;

const Date = styled.div`
  color: ${Theme.gray};
  font-size: 14px;
  margin-top: 3px;
`;

const Refunded = styled.span`
  color: red;
`;

const Cancelled = styled(CurrentPaymentLabel)`
  color: ${Theme.red};
`;

const SubscriptionsTableSection = styled.div`
  border: 1px solid #D7D7D7;
  border-radius: 8px;
  box-sizing: border-box;
  overflow: hidden;

  tbody tr:last-of-type td {
    border-bottom: none;
  }
`;

@observer
class PaymentHistory extends React.Component {
  @observable paymentPage = 1;

  @computed get rows() {
    const rows = (this.props.userSubscriptions || []).flatMap(subscription => {
      const payments = subscription.payments || [];
      if (!payments.length && subscription.pending && subscription.paymentMethod === 'paypal' &&
          !subscription.cancelAt && !subscription.cancelledAt?.date && !subscription.refunded) {
        return [{ subscription, pending: true, id: `pending-${subscription.id}`, date: subscription.createdAt?.date || subscription.currentPeriodStart?.date }];
      }
      return payments.map(payment => ({
        payment,
        subscription,
        id: payment.id,
        date: payment.occurredAt?.date || payment.createdAt?.date,
      }));
    });
    return orderBy(rows, [item => item.date?.getTime() || 0, 'id'], ['desc', 'desc']);
  }

  @computed get currentPaymentPage() {
    return Math.min(this.paymentPage, Math.max(1, Math.ceil(this.rows.length / PAYMENTS_PER_PAGE)));
  }

  @computed get currentPayment() {
    return this.rows.find(({ payment, subscription }) =>
      payment && subscription.id === this.props.activeSubscription?.id &&
      !payment.refunded &&
      payment.billingPeriodStart?.isPast &&
      payment.billingPeriodEnd?.isFuture,
    )?.payment;
  }

  @computed get paginatedRows() {
    const start = (this.currentPaymentPage - 1) * PAYMENTS_PER_PAGE;
    return this.rows.slice(start, start + PAYMENTS_PER_PAGE);
  }

  render() {
    if (!this.rows.length) return null;

    return (
      <Wrapper>
        {
          this.rows.length > 0 &&
          <SubscriptionsTableSection>
            <TableWrapper
              className='credit-cards'
              style={{ border: 'none' }}
            >
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {
                  this.paginatedRows.map(({ payment, subscription, pending, id }) => {
                    if (pending) {
                      return (
                        <PaymentRow key={id}>
                          <td>
                            <Plan>
                              {subscription.membership?.title?.replace(/<[^>]*>/g, '')}
                              <PendingPaymentLabel>Pending</PendingPaymentLabel>
                            </Plan>
                            <Date>{(subscription.createdAt?.date ? subscription.createdAt : subscription.currentPeriodStart)?.formattedDateWithYear}</Date>
                            <Date>PayPal can take up to 24 hours to send the initial payment.</Date>
                          </td>
                          <td>—</td>
                        </PaymentRow>
                      );
                    }
                    const isLatestPayment = this.rows.find(item => item.subscription.id === subscription.id)?.payment?.id === payment.id;
                    const isCancelled = subscription.cancelAt?.date || subscription.cancelledAt?.date || subscription.stripeStatus === 'canceled';
                    const periodStart = payment.billingPeriodStart?.formattedDateWithYear;
                    const periodEnd = payment.billingPeriodEnd?.date
                      ? payment.billingPeriodEnd.momentObj.subtract(1, 'day').format("MMM D 'YY")
                      : null;
                    const paymentDate = (payment.occurredAt?.date ? payment.occurredAt : payment.createdAt)?.formattedDateWithYear;
                    return (
                      <PaymentRow key={payment.id} aria-current={payment.id === this.currentPayment?.id ? 'true' : undefined}>
                        <td>
                          <Plan>
                            {subscription.membership?.title?.replace(/<[^>]*>/g, '')}
                            {isLatestPayment && isCancelled && <Cancelled>Cancelled</Cancelled>}
                            {payment.id === this.currentPayment?.id && <CurrentPaymentLabel>Current</CurrentPaymentLabel>}
                          </Plan>
                          <Date>
                            {periodStart && periodEnd ? `${periodStart} - ${periodEnd}` : paymentDate}
                            {payment.refunded && <Refunded> (Refunded)</Refunded>}
                          </Date>
                        </td>
                        <td>
                          {payment.displayAmountFormatted}
                        </td>
                      </PaymentRow>
                    );
                  })
                }
              </tbody>
            </TableWrapper>
          </SubscriptionsTableSection>
        }
        {
          this.rows.length > PAYMENTS_PER_PAGE &&
          <Paginator
            currentPage={this.currentPaymentPage}
            totalCount={this.rows.length}
            limit={PAYMENTS_PER_PAGE}
            onPagination={increment => this.paymentPage = this.currentPaymentPage + increment}
            hideInput
            margin='0'
          />
        }
      </Wrapper>
    );
  }
}

export default PaymentHistory;
